import { useState } from "react";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { ServicePath } from "../../constants/apiConstants";
import { useDynamicFetchMutation } from "../core/rest/useFetchMutation";
import type { Notification, NotificationsApiResponse, FetchNotificationsParams } from "./notifications.types";
import { notificationsKeys } from "./notifications.queryKeys";

function markItemRead(notificationId: string) {
  return (n: Notification): Notification => (n.id === notificationId ? { ...n, read: true } : n);
}

function applyOptimisticRead(
  notificationId: string,
  old: InfiniteData<NotificationsApiResponse> | undefined,
): InfiniteData<NotificationsApiResponse> | undefined {
  if (!old) return old;
  const markRead = markItemRead(notificationId);
  return {
    ...old,
    pages: old.pages.map((page) => ({ ...page, items: page.items.map(markRead) })),
  };
}

export function useNotificationsMutation(
  baseUrl: string,
  entityId: string,
  notificationId: string,
  params?: FetchNotificationsParams,
) {
  const queryClient = useQueryClient();

  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: notificationsKeys.infiniteListByEntity(entityId) });

  const invalidateUnreadCount = () =>
    queryClient.invalidateQueries({ queryKey: notificationsKeys.unreadCount(entityId) });

  const query = new URLSearchParams({ entityId });

  const mutation = useDynamicFetchMutation<undefined, undefined>({
    mutationKey: ["updateNotificationRead", entityId, notificationId],
    method: "POST",
    options: {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: notificationsKeys.infiniteListByEntity(entityId) });
        queryClient.setQueryData<InfiniteData<NotificationsApiResponse>>(
          notificationsKeys.infiniteList(entityId, params ?? {}),
          (old) => applyOptimisticRead(notificationId, old),
        );
      },
      onError: (): Promise<void> => invalidateList(),
      // Invalidate both the list (prefix key — covers every active filter
      // combination) and the unread count. Without the list invalidation the
      // notification would remain visually unread in the "Unread only" view
      // because the optimistic write targets the exact key infiniteList(entityId,
      // params ?? {}) which is a silent no-op when the caller omits params.
      onSuccess: (): Promise<void> => Promise.all([invalidateList(), invalidateUnreadCount()]).then(() => undefined),
    },
  });

  return {
    markAsRead: () => {
      mutation.mutate({
        url: `${baseUrl}${ServicePath.GlobalNavNotificationsServiceGraphql}/${notificationId}/read?${query.toString()}`,
      });
    },
    markAsReadAsync: mutation.mutateAsync,
    isMarkingAsRead: mutation.isLoading,
  };
}

/**
 * Bulk mutation hook for marking multiple notifications as read.
 *
 * Fires concurrent POST requests for each provided notification ID, then
 * invalidates the list and unread-count caches so all subscribers reflect
 * the new state. Loading state is tracked explicitly so it remains accurate
 * across concurrent calls (React Query mutation state only reflects the last call).
 */
export function useBulkMarkAsReadMutation(baseUrl: string, entityId: string) {
  const queryClient = useQueryClient();
  const [isMarkingAsRead, setIsMarkingAsRead] = useState(false);

  const mutation = useDynamicFetchMutation<undefined, undefined>({
    mutationKey: ["bulkMarkAsRead", entityId],
    method: "POST",
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: notificationsKeys.infiniteListByEntity(entityId) }),
      queryClient.invalidateQueries({ queryKey: notificationsKeys.unreadCount(entityId) }),
    ]);

  const markAllAsRead = async (notificationIds: string[]): Promise<void> => {
    if (notificationIds.length === 0) return;
    const query = new URLSearchParams({ entityId });
    setIsMarkingAsRead(true);
    try {
      // allSettled so a single failed POST does not reject the whole batch —
      // the function always resolves, preventing unhandled-rejection noise at
      // every call site (window.onunhandledrejection / error monitoring).
      await Promise.allSettled(
        notificationIds.map((id) =>
          mutation.mutateAsync({
            url: `${baseUrl}${ServicePath.GlobalNavNotificationsServiceGraphql}/${id}/read?${query.toString()}`,
          }),
        ),
      );
    } finally {
      setIsMarkingAsRead(false);
      void invalidate();
    }
  };

  return { markAllAsRead, isMarkingAsRead };
}
